import { useId, useState } from 'react';

import { Button, Card, Field, formatEuro, Input, Select, Textarea } from '@/design-system';
import {
    FINDING_SEVERITY_LABELS,
    isManualSeverity,
    MANUAL_SEVERITIES,
    parseEuroToCents,
    URGENCY_LABELS,
    type CostCents,
    type ManualSeverity,
} from '@/entities/finding';

import type { PhotoRecord } from '@/shared/offline';

import { surveyCopy } from '../copy';
import { usePhotoDrop } from '../hooks/usePhotoDrop';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { DropOverlay, PhotoButtons } from './PhotoPicker';
import { PhotoStrip } from './PhotoStrip';
import { VoiceButton } from './VoiceButton';

export interface ManualFindingDraft {
    /**
     * Het onderdeel waar de bevinding bij hoort: de id van de knoop in de boom, of
     * `''` voor een bestaande bevinding zonder (bestaand) onderdeel. Het onderdeel
     * bepaalt waar de bedragen meetellen en onder welke kop het rapport hem zet.
     */
    place: string;
    description: string;
    /** Waar het gebrek zit, in een handvol woorden; leeg als de inspecteur niets zei. */
    location: string;
    severity: ManualSeverity;
    costs: CostCents;
}

export interface ManualFindingFormProps {
    /** De onderdelen van de opname, in boomvolgorde: `{ value: knoop-id, label }`. */
    places: readonly { value: string; label: string }[];
    initial: ManualFindingDraft;
    /** Hoe het onderdeel heet als het niet in de lijst staat, zoals een instantie die er niet meer is. */
    placeLabel?: string;
    /**
     * De foto's van deze bevinding: toevoegen met de camera, uit de bibliotheek of
     * door ze op het formulier te slepen, en weer weghalen. Ze gaan naar de server
     * zodra de bevinding daar staat.
     */
    photos: {
        items: readonly PhotoRecord[];
        onAdd: (files: File[]) => void;
        onRemove: (clientUuid: string) => void;
    };
    isNew: boolean;
    onSave: (draft: ManualFindingDraft) => void;
    onRemove?: () => void;
    onCancel: () => void;
}

const COST_FIELDS = ['direct', 'term', 'improvement'] as const;

function euro(cents: number): string {
    return cents === 0 ? '' : formatEuro(Math.round(cents / 100));
}

/**
 * Een bevinding die niet uit een antwoord volgt: onderdeel, omschrijving,
 * gebrek of aandachtspunt, en de kosten per urgentie. Alleen deze bevindingen
 * zijn vanuit de app te wijzigen; de API weigert elke andere.
 */
export function ManualFindingForm({
    places,
    initial,
    placeLabel,
    photos,
    isNew,
    onSave,
    onRemove,
    onCancel,
}: ManualFindingFormProps) {
    const copy = surveyCopy.manual;
    const titleId = useId();
    const drop = usePhotoDrop(photos.onAdd);
    const [place, setPlace] = useState(initial.place);
    const [description, setDescription] = useState(initial.description);
    const [location, setLocation] = useState(initial.location);
    const [severity, setSeverity] = useState<ManualSeverity>(initial.severity);
    const [costs, setCosts] = useState<Record<(typeof COST_FIELDS)[number], string>>({
        direct: euro(initial.costs.direct),
        term: euro(initial.costs.term),
        improvement: euro(initial.costs.improvement),
    });
    const [confirmRemove, setConfirmRemove] = useState(false);
    const voice = useVoiceInput((transcript) =>
        setLocation((current) => (current.trim() === '' ? transcript : `${current} ${transcript}`)),
    );

    const options = places.some((option) => option.value === initial.place)
        ? places
        : [{ value: initial.place, label: placeLabel || surveyCopy.findings.noPlace }, ...places];
    const canSave = description.trim() !== '';

    return (
        <Card
            padding="md"
            role="group"
            aria-labelledby={titleId}
            data-dropzone={drop.active ? 'active' : undefined}
            className="relative"
            {...drop.handlers}
        >
            {drop.active ? <DropOverlay /> : null}

            <h3
                id={titleId}
                className="m-0 mb-3 font-[family-name:var(--font-display)] text-[15px] font-semibold text-[var(--text-primary)]"
            >
                {isNew ? copy.newTitle : copy.editTitle}
            </h3>

            <div className="flex flex-col gap-[10px]">
                <Field label={copy.place}>
                    <Select value={place} onChange={(event) => setPlace(event.target.value)}>
                        {options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </Field>

                <Field label={copy.description}>
                    <Textarea
                        value={description}
                        placeholder={copy.descriptionPlaceholder}
                        onChange={(event) => setDescription(event.target.value)}
                    />
                </Field>

                <div className="flex items-end gap-[10px]">
                    <Field label={copy.location} className="min-w-0 flex-1">
                        <Input
                            placeholder={copy.locationPlaceholder}
                            value={location}
                            onChange={(event) => setLocation(event.target.value)}
                        />
                    </Field>

                    <VoiceButton
                        voice={voice}
                        idle={surveyCopy.detail.locationVoiceIdle}
                        recording={surveyCopy.detail.voiceRecording}
                        unavailable={surveyCopy.detail.locationVoiceUnavailable}
                    />
                </div>

                <Field label={copy.severity}>
                    <Select
                        value={severity}
                        onChange={(event) => {
                            if (isManualSeverity(event.target.value)) {
                                setSeverity(event.target.value);
                            }
                        }}
                    >
                        {MANUAL_SEVERITIES.map((option) => (
                            <option key={option} value={option}>
                                {FINDING_SEVERITY_LABELS[option]}
                            </option>
                        ))}
                    </Select>
                </Field>

                <div className="flex flex-col gap-[6px]">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[12px] text-[var(--text-secondary)]">
                            {copy.photos}
                        </span>
                        <div className="flex flex-wrap items-center gap-[7px]">
                            <PhotoButtons count={photos.items.length} onAddPhotos={photos.onAdd} />
                        </div>
                    </div>
                    {photos.items.length === 0 ? (
                        <p className="m-0 text-[12px] text-[var(--text-tertiary-safe)]">
                            {copy.photosEmpty}
                        </p>
                    ) : (
                        <PhotoStrip
                            photos={photos.items}
                            onAddPhotos={photos.onAdd}
                            onRemovePhoto={photos.onRemove}
                        />
                    )}
                </div>

                <div className="grid gap-[10px] [grid-template-columns:repeat(auto-fit,minmax(110px,1fr))]">
                    {COST_FIELDS.map((field) => (
                        <Field key={field} label={URGENCY_LABELS[field]}>
                            <Input
                                mono
                                inputMode="numeric"
                                value={costs[field]}
                                placeholder="€ 0"
                                onChange={(event) =>
                                    setCosts((current) => ({
                                        ...current,
                                        [field]: event.target.value,
                                    }))
                                }
                            />
                        </Field>
                    ))}
                </div>
            </div>

            {confirmRemove && onRemove ? (
                <div className="mt-[14px] flex flex-wrap items-center justify-between gap-2 rounded-[7px] border border-[var(--border-default)] bg-[var(--surface-detail)] px-3 py-2">
                    <span className="text-[13px] text-[var(--text-primary)]">
                        {copy.confirmRemove}
                    </span>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="md" onClick={() => setConfirmRemove(false)}>
                            {copy.keep}
                        </Button>
                        <Button variant="warn" size="md" onClick={onRemove}>
                            {copy.remove}
                        </Button>
                    </div>
                </div>
            ) : null}

            <div className="mt-[14px] flex flex-wrap justify-between gap-2">
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="primary"
                        size="md"
                        disabled={!canSave}
                        onClick={() =>
                            onSave({
                                place,
                                description: description.trim(),
                                location: location.trim(),
                                severity,
                                costs: {
                                    direct: parseEuroToCents(costs.direct) ?? 0,
                                    term: parseEuroToCents(costs.term) ?? 0,
                                    improvement: parseEuroToCents(costs.improvement) ?? 0,
                                },
                            })
                        }
                    >
                        {copy.save}
                    </Button>
                    <Button variant="outline" size="md" onClick={onCancel}>
                        {copy.cancel}
                    </Button>
                </div>
                {onRemove && !confirmRemove ? (
                    <Button
                        variant="outline"
                        size="md"
                        icon="delete"
                        onClick={() => setConfirmRemove(true)}
                    >
                        {copy.remove}
                    </Button>
                ) : null}
            </div>
        </Card>
    );
}
